import { getLocation, push, RouterLocation } from "connected-react-router";
import { omit } from "lodash-es";
import { all, call, fork, put, select, take, takeLatest } from "redux-saga/effects";
import { isActionOf } from "typesafe-actions";

import BoundaryType from "@mapmycustomers/shared/enum/BoundaryType";
import EntityType from "@mapmycustomers/shared/enum/EntityType";
import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import Located, {
  GeocodeResult,
  GeoManagementState,
} from "@mapmycustomers/shared/types/base/Located";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { EntityTypesSupportedByMapsPage, MapEntity } from "@mapmycustomers/shared/types/entity";
import { MapRecordsResponse } from "@mapmycustomers/shared/types/map";
import Organization from "@mapmycustomers/shared/types/Organization";
import PersistentBaseMapLayerData from "@mapmycustomers/shared/types/persistent/PersistentBaseMapLayerData";
import PersistentMapSettings from "@mapmycustomers/shared/types/persistent/PersistentMapSettings";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";
import PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import {
  convertCoordinatesToGeoPoint,
  convertPlatformBoundsToLatLngBounds,
} from "@mapmycustomers/shared/util/geo/GeoService";

import localSettings from "@app/config/LocalSettings";
import MapMode from "@app/enum/map/MapMode";
import Path from "@app/enum/Path";
import LassoToolMode from "@app/scene/map/enums/LassoToolMode";
import { groupModeSagas } from "@app/scene/map/store/groupMode/sagas";
import { leadFinderModeSagas } from "@app/scene/map/store/leadFinderMode/sagas";
import { mapModeSagas } from "@app/scene/map/store/mapMode/sagas";
import { pinLocationSagas } from "@app/scene/map/store/pinLocation/sagas";
import {
  doesEntityViewHasChanges,
  getChangesDiscarded,
  getExcludedRecords,
  getIncludedRecords,
  getLassoMode,
  getMapLassoPaths,
  getMapMode,
  getMapViewState,
  getMapViewTool,
  getRecordsListState,
  getUserLocationsSettings,
  getVisibleLassoEntityTypes,
  isSidebarVisible,
} from "@app/scene/map/store/selectors";
import { territoryModeSagas } from "@app/scene/map/store/territoryMode/sagas";
import { toolbarSagas } from "@app/scene/map/store/toolbar/sagas";
import { FIT_TO_PINS_URL_PARAM } from "@app/scene/map/utils/fitToPinsUrlParam";
import { callApi } from "@app/store/api/callApi";
import { hideEntityView } from "@app/store/entityView/actions";
import { handleError } from "@app/store/errors/actions";
import { getMapSettings, getOrganizationId, getPosition } from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import { getMapViewSettings } from "@app/store/map";
import { openOnMapPage } from "@app/store/map/actions";
import { getMapRecordsListSettings } from "@app/store/map/selectors";
import { boundariesViewSettingsChanged } from "@app/store/mapLayers/boundaries/actions";
import { getBoundaryType } from "@app/store/mapLayers/boundaries/selectors";
import { setUserLocationsSettings } from "@app/store/mapLayers/userLocations/actions";
import { selectSavedFilter } from "@app/store/savedFilters/actions";
import GeoPath from "@app/types/GeoPath";
import MapPersistedViewportState from "@app/types/map/MapPersistedViewportState";
import MapRecordsListState from "@app/types/viewModel/MapRecordsListState";
import { mapEntityIdGetter } from "@app/util/map/idGetters";
import { mapEntityIdParser } from "@app/util/map/idParsers";
import isValidMapEntry from "@app/util/map/isValidMapEntry";
import convertMapFilterToPlatformFilterModel from "@app/util/viewModel/convertMapFilterToPlatformFilterModel";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";

import {
  applyMapViewSettings,
  applyRecordsListSettings,
  applyTerritoryMapViewSettings,
  cleanLassoPaths,
  clearHighlight,
  clearRecordsList,
  clearSelection,
  enterLassoMode,
  enterMode,
  exitLassoMode,
  exitMode,
  exitRulerMode,
  fetchGroupPins,
  fetchLassoSelection,
  fetchPins,
  fetchRecords,
  fetchTerritoryPins,
  hideAnnotation,
  hideMapEntityView,
  initializeMapView,
  refreshLassoMode,
  resetRecordsListPagination,
  restartSelection,
  selectMapTool,
  setChangesDiscarded,
  setLassoContainsFlags,
  setMapSettings,
  setPinHover,
  setSelection,
  setSelectionEntities,
  showSidebar,
  updateLassoClusters,
  updatePinDropCoordinates,
  updateRulerPath,
} from "./actions";

export function* onInitializeMapView({
  payload: callback,
}: ReturnType<typeof initializeMapView.request>) {
  const viewState: Partial<MapViewState> = yield select(getMapViewSettings);

  yield put(applyMapViewSettings(viewState));

  if (viewState.boundaryKey) {
    // Split the boundariesViewSettingsChanged dispatch into two — non-persisted first,
    // persisted last — so each one only triggers one fetchBoundaryStats.request and the
    // reducer's `type: payload.boundaryType` writes land in deterministic order. The
    // persisted type's write is last and wins.
    const persistedType: BoundaryType = yield select(getBoundaryType);
    const { boundaryKey } = viewState;
    const otherType =
      persistedType === BoundaryType.TERRITORY ? BoundaryType.GEOGRAPHY : BoundaryType.TERRITORY;
    if (boundaryKey[otherType]) {
      yield put(
        boundariesViewSettingsChanged({ boundaryKey: { [otherType]: boundaryKey[otherType] } })
      );
    }
    if (boundaryKey[persistedType]) {
      yield put(
        boundariesViewSettingsChanged({
          boundaryKey: { [persistedType]: boundaryKey[persistedType] },
        })
      );
    }
  }

  const recordsListState: MapRecordsListState = yield select(getMapRecordsListSettings);
  if (recordsListState) {
    yield put(applyRecordsListSettings(recordsListState));
  }

  // initialize settings

  const mapSettings: Partial<PersistentMapSettings> | undefined = yield select(getMapSettings);
  yield put(setMapSettings(mapSettings));

  const userLocationsSettings: PersistentBaseMapLayerData | undefined =
    yield select(getUserLocationsSettings);
  yield put(setUserLocationsSettings(userLocationsSettings));

  const viewportState: MapPersistedViewportState = yield call(localSettings.getViewportState);
  if (viewportState.center || viewportState.zoom) {
    callback(viewportState);
    yield put(initializeMapView.success());
    return;
  }

  const position: GeolocationPosition | undefined = yield select(getPosition);
  if (position) {
    const bounds = new google.maps.LatLngBounds();
    bounds.extend({ lat: position.coords.latitude, lng: position.coords.longitude });
    const center = bounds.getCenter();
    callback({ center: [center.lng(), center.lat()], zoom: 15 });
    yield put(initializeMapView.success());
    return;
  }
  callback({ center: [0, 0], zoom: 2 });

  yield put(initializeMapView.success());
}

export function* onPinDropGeocode({
  payload,
}: ReturnType<typeof updatePinDropCoordinates.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    if (payload) {
      const geoPoint = convertCoordinatesToGeoPoint(payload);
      const result: GeocodeResult = yield callApi("reverseGeocodeAddress", orgId, geoPoint);

      const geocodedLocation: Located = {
        ...result.address,
        geoAddress: result.address,
        geoCodedAt: null,
        geoCodeType: null,
        geoManagementState: GeoManagementState.MANUAL,
        geoPoint,
        geoSource: null,
        geoStatus: null,
      };

      yield put(updatePinDropCoordinates.success(geocodedLocation));
    } else {
      yield put(updatePinDropCoordinates.success(undefined));
    }
  } catch (error) {
    yield put(updatePinDropCoordinates.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onEnterLassoMode() {
  try {
    yield put(restartSelection());
    yield put(clearRecordsList());
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onExitLassoMode() {
  try {
    // Clear selected map tool
    yield put(selectMapTool.request(undefined));

    yield put(cleanLassoPaths());

    const mode: MapMode = yield select(getMapMode);
    if (mode !== MapMode.LEAD_FINDER) {
      yield put(restartSelection());
    }
    yield put(refreshLassoMode());
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onExitRulerMode() {
  yield put(selectMapTool.request(undefined));
  yield put(updateRulerPath([]));
}

export function* onRefreshLassoMode() {
  try {
    const mode: MapMode = yield select(getMapMode);
    switch (mode) {
      case MapMode.GROUPS:
        yield put(fetchGroupPins.request({ request: {} }));
        break;

      case MapMode.TERRITORIES:
        yield put(fetchTerritoryPins.request({ request: {} }));
        break;

      default:
        yield put(fetchPins.request({ request: {} }));
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onUpdateLassoClusters({ payload }: ReturnType<typeof updateLassoClusters>) {
  try {
    const activeTool: MapTool | undefined = yield select(getMapViewTool);
    const isLassoMode = activeTool === MapTool.LASSO;

    let containsClusters = false;
    if (isLassoMode && payload && payload.length > 0) {
      const paths: Array<GeoPath> = yield select(getMapLassoPaths);

      if (paths.length > 0) {
        const area = new google.maps.Polygon({ paths });
        const bounds = new google.maps.LatLngBounds();

        if (area) {
          area
            .getPath()
            .forEach((item) => bounds.extend(new google.maps.LatLng(item.lat(), item.lng())));

          const matches = payload
            .filter((cluster) => isValidMapEntry(cluster) && cluster.bounds)
            .filter((cluster) => {
              // we just ensured above that cluster.bounds is defined
              const clusterBounds = convertPlatformBoundsToLatLngBounds(cluster.bounds!);

              return (
                bounds.intersects(clusterBounds) ||
                google.maps.geometry.poly.containsLocation(
                  // we just ensured above that cluster.region is defined
                  { lat: cluster.region!.latitude, lng: cluster.region!.longitude },
                  area
                )
              );
            });

          containsClusters = matches.length > 0;
        }
      }
    }
    yield put(setLassoContainsFlags({ containsClusters }));
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onFetchLassoSelection({
  payload,
}: ReturnType<typeof fetchLassoSelection.request>) {
  try {
    yield put(showSidebar({ skipLoading: true }));

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const mapViewState: MapViewState = yield select(getMapViewState);
    const recordsListState: MapViewState = yield select(getRecordsListState);

    const sidebarVisible: boolean = yield select(isSidebarVisible);
    const activeTool: MapTool | undefined = yield select(getMapViewTool);
    const lassoPaths: Array<GeoPath> = yield select(getMapLassoPaths);

    const lassoVisibleEntities: EntityTypesSupportedByMapsPage[] = yield select(
      getVisibleLassoEntityTypes
    );
    const lassoMode: LassoToolMode = yield select(getLassoMode);
    const isLassoMode = activeTool === MapTool.LASSO;

    const visibleEntityTypes =
      isLassoMode && lassoMode === LassoToolMode.ADD_GROUP_PINS
        ? lassoVisibleEntities
        : mapViewState.visibleEntities;

    const mapFilters = convertMapFilterToPlatformFilterModel(
      mapViewState.filter,
      visibleEntityTypes
    );

    const hasLasso = isLassoMode && lassoPaths.length;

    if (hasLasso) {
      const multipolygon: LongLat[][] = payload.request.lasso
        ? payload.request.lasso
        : lassoPaths.map((path) =>
            path.map((item: google.maps.LatLng): LongLat => [item.lng(), item.lat()])
          );

      const requestPayload = {
        $filters: {
          ...mapFilters,
          includeAccessStatus: true,
          includeAssociations: true,
          includeCustomFields: true,
          includeEmailSubscriptionStatus: true,
          multipolygon,
          skipAccessibleCount: true,
        },
        $limit: 10000,
        $offset: 0,
        $order: recordsListState.sort
          ? convertToPlatformSortModel(recordsListState.sort)
          : undefined,
      };

      const response: MapRecordsResponse = yield callApi("fetchMapPins", orgId, requestPayload);
      const responseItems = response.data ?? [];

      const excludedRecords: Set<string> = yield select(getExcludedRecords);
      const includedRecords: Set<string> = yield select(getIncludedRecords);

      const itemsWithoutExcluded = new Set(
        responseItems
          .map((entity) => mapEntityIdGetter(entity))
          .filter((value) => !excludedRecords.has(value))
      );
      const itemsWithIncluded = new Set([
        ...Array.from(includedRecords),
        ...Array.from(itemsWithoutExcluded),
      ]);
      yield put(setSelection(itemsWithIncluded));

      const selectionEntities = responseItems.filter(
        (entity) => !excludedRecords.has(mapEntityIdGetter(entity))
      );

      if (includedRecords.size) {
        // Inject filtering by manually added entity IDs
        if (mapFilters.entities && !Array.isArray(mapFilters.entities)) {
          const entityIdFilter: Array<MapEntity["id"]> = [];
          for (const mapEntityId of includedRecords.values()) {
            const parsed = mapEntityIdParser(mapEntityId);
            if (parsed) {
              entityIdFilter.push(parsed.id);
            }
          }

          const entities = mapFilters.entities as Record<
            EntityTypesSupportedByMapsPage,
            PlatformFilterModel
          >;
          (Object.keys(entities) as EntityTypesSupportedByMapsPage[]).forEach((entityKey) => {
            if (!("$and" in entities[entityKey])) {
              entities[entityKey].$and = [];
            }
            const andCondition = entities[entityKey].$and;
            if (Array.isArray(andCondition) && Array.isArray(entityIdFilter)) {
              andCondition.push({ id: { $in: entityIdFilter } });
            }
          });
        }

        const requestPayload = {
          $filters: { ...mapFilters, skipAccessibleCount: true },
          $limit: 10000,
          $offset: 0,
        };

        const response: MapRecordsResponse = yield callApi("fetchMapPins", orgId, requestPayload);
        yield put(setSelectionEntities([...selectionEntities, ...(response.data ?? [])]));
      } else {
        yield put(setSelectionEntities(selectionEntities));
      }

      if (itemsWithIncluded.size && !sidebarVisible) {
        yield put(clearRecordsList());
      }
    } else {
      yield put(clearSelection());
    }

    if (sidebarVisible) {
      yield put(resetRecordsListPagination());
      yield put(fetchRecords.request({ request: {} }));
    }

    yield put(fetchLassoSelection.success());
  } catch (error) {
    yield put(fetchLassoSelection.failure(error));
    yield put(handleError({ error }));
  }
}

// This is called when you switch between map modes.
// Because we don't have design limitation regarding when and how user can trigger
// mode change - we need to have proper cleanup upon both enter and exit events.
export function* onSwitchMode(action: ReturnType<typeof enterMode | typeof exitMode>) {
  try {
    // when entering territory mode, copy color/shapeKey from the map mode
    if (isActionOf(enterMode, action) && action.payload === MapMode.TERRITORIES) {
      const viewState: MapViewState = yield select(getMapViewState);
      yield put(
        applyTerritoryMapViewSettings({
          colorKey: viewState.colorKey,
          shapeKey: viewState.shapeKey,
        })
      );
    }

    // Clear pin hover
    yield put(setPinHover(undefined));
    // Clear annotation
    yield put(hideAnnotation());
    // Clear currently viewed entity
    yield put(hideEntityView());
    // Clear selected map tool
    yield put(selectMapTool.request(undefined));

    // Clear all records in sidebar, since they're very likely to be changed
    yield put(clearRecordsList());

    // Cleanup of lasso
    yield put(cleanLassoPaths());

    // Cleanup of selected and highlighted items
    yield put(clearHighlight());
    yield put(clearSelection());
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onHideEntityView() {
  yield put(setPinHover(undefined));
}

export function* onOpenOnMapPage({ payload }: ReturnType<typeof openOnMapPage>) {
  const { pathname }: RouterLocation<unknown> = yield select(getLocation);
  const path = payload.fitToPins ? `${Path.MAP}?${FIT_TO_PINS_URL_PARAM}` : Path.MAP;

  // TODO: consider updating MapViewFilterButton to read savedFilter from map's viewState instead of savedFilters store
  // reset saved filters we had there before
  yield put(selectSavedFilter({ entityType: EntityType.PIN, savedFilter: undefined }));

  // Already on the map, the page does not remount, so init never re-applies saved settings.
  if (pathname.startsWith(Path.MAP) && !payload.openOnNewTab) {
    yield put(fetchPins.request({ request: payload.mapViewState }));
    yield put(push(path));
    return;
  }

  const mapViewState: MapViewState = yield select(getMapViewState);
  const updatedMapViewState = {
    ...mapViewState,
    ...payload.mapViewState,
  };
  yield put(
    updateMetadata.request({
      mapViewSettings2: omit(updatedMapViewState, "lasso"),
    })
  );
  yield take([updateMetadata.success, updateMetadata.failure]);

  if (payload.viewport) {
    yield call(localSettings.updateViewportState, payload.viewport);
  }

  if (payload.openOnNewTab) {
    const url = `${window.location.origin}${path}`;
    yield call(window.open, url, "_blank");
  } else {
    yield put(push(path));
  }
}

export function* onSelectMapTool({ payload }: ReturnType<typeof selectMapTool.request>) {
  if (payload?.mapTool === MapTool.LASSO) {
    const hasChanges: boolean = yield select(doesEntityViewHasChanges);

    yield put(hideMapEntityView());

    if (hasChanges) {
      yield take(setChangesDiscarded);

      const changesDiscarded: boolean = yield select(getChangesDiscarded);
      if (!changesDiscarded) {
        yield put(selectMapTool.failure());
        return;
      }
    }
  }

  yield put(selectMapTool.success(payload));
}

export function* defaultSagas() {
  yield takeLatest(initializeMapView.request, onInitializeMapView);
  yield takeLatest(updatePinDropCoordinates.request, onPinDropGeocode);
  yield takeLatest(fetchLassoSelection.request, onFetchLassoSelection);
  yield takeLatest(enterMode, onSwitchMode);
  yield takeLatest(exitMode, onSwitchMode);
  yield takeLatest(hideEntityView, onHideEntityView);
  yield takeLatest(updateLassoClusters, onUpdateLassoClusters);
  yield takeLatest(enterLassoMode, onEnterLassoMode);
  yield takeLatest(exitLassoMode, onExitLassoMode);
  yield takeLatest(openOnMapPage, onOpenOnMapPage);
  yield takeLatest(refreshLassoMode, onRefreshLassoMode);
  yield takeLatest(selectMapTool.request, onSelectMapTool);
  yield takeLatest(exitRulerMode, onExitRulerMode);
}

export function* mapSaga() {
  yield all([
    fork(defaultSagas),
    fork(mapModeSagas),
    fork(groupModeSagas),
    fork(leadFinderModeSagas),
    fork(pinLocationSagas),
    fork(territoryModeSagas),
    fork(toolbarSagas),
  ]);
}
